<template>
    <div>
        <div class="swiper swiper-container ob1-carrousel mb-0" tabindex="-1" :id="uniqSwiperId" :style="cssVars">
            <div :id="uniqSwiperWrapperId" class="swiper-wrapper">
                <slot></slot>
            </div>
            <div v-if="scrollbarConfiguration" class="swiper-scrollbar"></div>
            <div v-if="paginationConfiguration" class="row">
                <ol
                    class="swiper-pagination ob1-page-indicator"
                    :class="{ 'd-none': swipeContentTooSmallToSwipe }"
                    aria-hidden="true"
                />
            </div>
            <div :id="uniqSwiperNavigationId" class="swiper-navigation" :class="{ 'd-none': !navigation || swipeContentTooSmallToSwipe }">
                <button
                    class="swiper-button-prev"
                    :class="{
                        'd-none': hideNavigationForSmallScreens,
                        'd-md-block': hideNavigationForSmallScreens,
                        'swiper-button-hidden-when-disabled': hideDisabledNavigationPrevEl
                    }"
                    title="Précédent"
                >
                    <span class="visually-hidden">Précédent</span>
                </button>
                <button
                    class="swiper-button-next"
                    :class="{
                        'd-none': hideNavigationForSmallScreens,
                        'd-md-block': hideNavigationForSmallScreens,
                        'swiper-button-hidden-when-disabled': hideDisabledNavigationNextEl
                    }"
                    title="Suivant"
                >
                    <span class="visually-hidden">Suivant</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    name: "SwiperContainer",
    props: {
        uniqSwiperId: {
            type: String,
            required: true
        },
        spaceBetween: {
            type: Number,
            default: 20
        },
        centeredSlides: {
            type: Boolean,
            default: false
        },
        slidesPerView: {
            type: Number,
            default: 1.5
        },
        loop: {
            type: Boolean,
            default: false
        },

        // Responsive breakpoints
        breakpoints: {
            type: Object,
            default(rawProps) {
                return {}
            }
        },

        // enable accessibility
        a11y: {
            type: Boolean,
            default: true
        },
        allowTouchMove: {
            type: Boolean,
            default: true
        },
        keyboard: {
            type: Boolean,
            default: true
        },
        keyboardEnabled: {
            type: Boolean,
            default: true
        },
        keyboardOnlyInViewport: {
            type: Boolean,
            default: false
        },
        pagination: {
            type: Boolean,
            default: true
        },
        paginationType: {
            type: String,
            default: 'bullets'
        },
        paginationBulletClass: {
            type: String,
            default: 'ob1-page-indicator-item'
        },
        paginationBulletActiveClass: {
            type: String,
            default: 'ob1-page-indicator-active'
        },
        paginationClickable: {
            type: Boolean,
            default: true
        },
        scrollbar: {
            type: Boolean,
            default: false
        },
        scrollbarDraggable: {
            type: Boolean,
            default: true
        },
        scrollbarDragClass: {
            type: String,
            default: 'swiper-scrollbar-drag bg-primary'
        },
        freeMode: {
            type: Boolean,
            default: false
        },
        autoplay: {
            type: Boolean,
            default: false
        },
        navigation: {
            type: Boolean,
            default: false
        },
        hideNavigationForSmallScreens: {
            type: Boolean,
            default: true
        },
        hideDisabledNavigationPrevEl: {
            type: Boolean,
            default: true
        },
        hideDisabledNavigationNextEl: {
            type: Boolean,
            default: true
        },
        updateNavigationPositionInScreen: {
            type: Boolean,
            default: false
        },
        updateNavigationPositionMarginInPx: {
            type: Number,
            default: 100
        },
        spaceBetweenSwiperSlidesAndSwiperScrollbarInPx: {
            type: Number,
            default: 15
        },
        spaceBetweenSwiperSlidesAndSwiperPaginationInPx: {
            type: Number,
            default: 30
        },
        paddingBottomInPx: {
            type: Number,
            default: 30
        }
    },
    data() {
        return {
            swiper: null,
            initSwiperConfiguration: null,
            onConfiguration: {
                slidesGridLengthChange: (swiper) => {
                    this.$emit("after-slides-grid-length-change")
                },
                snapGridLengthChange: (swiper) => {
                    this.updateSwipeOptions(swiper);
                },
                update: () => {
                    this.$emit("after-swiper-update");
                },
                resize: (_swiper) => {
                    // Fired when swiper content is resized
                },
                touchMove: (swiper, e) => {
                    // Fired on swiping
                    this.$emit("touch-move");
                },
                touchEnd: (swiper, e) => {
                    // Fired on end of swiping
                    this.$emit("touch-end");
                },
            },
            navigationNextEl: ".swiper-button-next",
            navigationPrevEl: ".swiper-button-prev",
            updateNavigationPositionTicking: false,
            paginationEl: '.swiper-pagination',
            paginationRenderBullet: function (index, className) {
                return '<li class="' + className + '"><button class="ob1-page-indicator-button" type="button" tabindex="-1"></button></li>';
            },
            scrollbarEl: '.swiper-scrollbar',
            swipeContentTooSmallToSwipe: true,
            initialSlide: undefined,
            initialSlideClass: 'initial-swiper-slide',
            isAlreadyLaunchingSwiperRefreshOnNextTick: false
        }
    },
    mounted() {
        this.initialSlide = this.getInitialSlideIndex();
        this.initSwiper();
        if (this.navigation && this.updateNavigationPositionInScreen) {
            window.addEventListener("scroll", this.onWindowScroll);
        }
    },
    beforeDestroy: function () {
        if (this.swiper) {
            this.swiper.destroy(true, true);
        }
        if (this.navigation && this.updateNavigationPositionInScreen) {
            window.removeEventListener("scroll", this.onWindowScroll);
        }
    },
    watch: {
        swiperConfiguration: function (_newVal, _oldVal) {
            this.reloadSwiper();
        },
    },
    computed: {
        uniqSwiperNavigationId() {
            return this.uniqSwiperId + "-navigation";
        },
        uniqSwiperWrapperId() {
            return this.uniqSwiperId + "-wrapper";
        },
        swiperContainerPaddingBottomInPx() {
            return this.paddingBottomInPx;
        },
        cssVars() {
            return {
                '--swiper-container-padding-bottom': this.swiperContainerPaddingBottomInPx + "px",
                '--swiper-scrollbar-margin-top': this.spaceBetweenSwiperSlidesAndSwiperScrollbarInPx + "px",
                '--swiper-pagination-padding-top': this.spaceBetweenSwiperSlidesAndSwiperPaginationInPx + "px",
            }
        },
        keyboardConfiguration() {
            if (this.keyboard) {
                return {
                    enabled: this.keyboardEnabled,
                    onlyInViewport: this.keyboardOnlyInViewport
                }
            } else {
                return false
            }
        },
        paginationConfiguration() {
            if (this.pagination) {
                return {
                    el: this.paginationEl,
                    type: this.paginationType,
                    bulletClass: this.paginationBulletClass,
                    bulletActiveClass: this.paginationBulletActiveClass,
                    clickable: this.paginationClickable,
                    renderBullet: this.paginationRenderBullet
                }
            } else {
                return false
            }
        },
        scrollbarConfiguration() {
            // scrollbar not displayed when pagination is displayed
            if ((this.scrollbar) && (!this.pagination)) {
                return {
                    el: this.scrollbarEl,
                    draggable: this.scrollbarDraggable,
                    dragClass: this.scrollbarDragClass
                }
            } else {
                return false
            }
        },
        navigationConfiguration() {
            if (this.navigation) {
                return {
                    nextEl: this.navigationNextEl,
                    prevEl: this.navigationPrevEl
                }
            } else {
                return false
            }
        },
        initialSlideConfiguration() {
            return this.initialSlide
        },
        swiperConfiguration() {
            return {
                allowTouchMove: this.allowTouchMove,
                spaceBetween: this.spaceBetween,
                centeredSlides: this.centeredSlides,
                slidesPerView: this.slidesPerView,
                loop: this.loop,
                breakpoints: this.breakpoints,
                a11y: this.a11y,
                keyboard: this.keyboardConfiguration,
                pagination: this.paginationConfiguration,
                scrollbar: this.scrollbarConfiguration,
                freeMode: this.freeMode,
                autoplay: this.autoplay,
                navigation: this.navigationConfiguration,
                on: this.onConfiguration,
                initialSlide: this.initialSlideConfiguration
            }
        }
    },
    methods: {
        getInitialSlideIndex() {
            const swiperSlots = this.$slots.default;
            const swiperSlides = swiperSlots.filter((swiperSlot) => {
                return (swiperSlot.elm) && (swiperSlot.elm.nodeType == Node.ELEMENT_NODE)
            })

            let returnedInitialSlideIndex = undefined;
            swiperSlides.forEach((swiperSlide, swiperSlideIndex) => {
                if (returnedInitialSlideIndex === undefined) {
                    const elementClassList = swiperSlide.elm.classList;
                    if (elementClassList.contains(this.initialSlideClass)) {
                        returnedInitialSlideIndex = swiperSlideIndex
                    }
                }
            })
            return returnedInitialSlideIndex;
        },
        onWindowScroll(event) {
            if (!this.updateNavigationPositionTicking) {
                this.updateNavigationPositionTicking = true;
                setTimeout(() => {
                    this.updateNavigationPosition();
                    this.updateNavigationPositionTicking = false;
                }, 150);
            }
        },
        updateNavigationPosition() {
            if (this.updateNavigationPositionInScreen && this.navigation && !this.swipeContentTooSmallToSwipe) {
                const wrapperEl = document.getElementById(this.uniqSwiperWrapperId);
                const elBottom = wrapperEl.getBoundingClientRect().bottom;
                const elTop = wrapperEl.getBoundingClientRect().top;
                const elHeight = elBottom - elTop;

                const visualViewportHeight = window.visualViewport.height;

                if ((elTop < window.visualViewport.height) && (elBottom > 0)) {
                    // Swiper is visible
                    const visibleElTop = (elTop > 0) ? elTop : 0;
                    const visibleElBottom = (elBottom < visualViewportHeight) ? elBottom : visualViewportHeight;
                    const visibleElHeight = visibleElBottom - visibleElTop;

                    // Define calculated topInPx
                    let topInPx = (visibleElTop - elTop) + (visibleElHeight / 2)

                    // Define positionMarginInPx max value from elHeight
                    const positionMarginInPx = (this.updateNavigationPositionMarginInPx < (elHeight / 2))
                            ? this.updateNavigationPositionMarginInPx : (elHeight / 2)

                    // Apply positionMarginInPx bounds for topInPx
                    if (topInPx < positionMarginInPx) {
                        topInPx = positionMarginInPx;
                    } else if (topInPx > (elHeight - positionMarginInPx)) {
                        topInPx = elHeight - positionMarginInPx;
                    }

                    // Update style top value only for 2px changes
                    const topInPxWithSteps = (parseInt("" + (topInPx / 2)) * 2);

                    const navigationEl = document.getElementById(this.uniqSwiperNavigationId);
                    navigationEl.style["top"] = topInPxWithSteps + 'px';
                } else {
                    this.resetNavigationPosition();
                }
            }
        },
        forceUpdatesOnNavigationPosition(times, delay) {
            const t = times - 1;
            setTimeout(() => {
                this.updateNavigationPosition();
                if ( t>0 ) {
                    this.forceUpdatesOnNavigationPosition(t, delay);
                }
            }, delay)
        },
        resetNavigationPosition() {
            const navigationEl = document.getElementById(this.uniqSwiperNavigationId);
            navigationEl.style["top"] = "";
        },
        updateSwipeOptions(swiper) {
            const newValSwipeContentTooSmallToSwipe = (!swiper)
                    || (!swiper.snapGrid)
                    || (swiper.snapGrid.length <= 1);
            if (this.swipeContentTooSmallToSwipe !== newValSwipeContentTooSmallToSwipe) {
                this.swipeContentTooSmallToSwipe = newValSwipeContentTooSmallToSwipe;
                // Emit some events when navigation buttons are external ( not implemented yet )
            }
            // Need to force this multiple updates to react on slides height changes
            this.forceUpdatesOnNavigationPosition(5, 100);
        },
        initSwiper() {
            this.initSwiperConfiguration = this.swiperConfiguration;
            this.swiper = new Swiper(`#${this.uniqSwiperId}.swiper-container`, this.initSwiperConfiguration);
            this.updateSwipeOptions(this.swiper);
        },
        reloadSwiper() {
            if (this.swiper) {
                this.swiper.destroy(true, true);
            }
            this.initSwiper();
        },
        swipeLeft() {
            if (this.swiper) {
                this.swiper.slidePrev();
            }
        },
        swipeRight() {
            if (this.swiper) {
                this.swiper.slideNext();
            }
        },
        canSwipeLeft() {
            if (this.swiper) {
                return !this.swipeContentTooSmallToSwipe && !this.swiper.isBeginning;
            }
            return false;
        },
        canSwipeRight() {
            if (this.swiper) {
                return !this.swipeContentTooSmallToSwipe && !this.swiper.isEnd;
            }
            return false;
        }
    },
    updated: function () {
        if (!this.isAlreadyLaunchingSwiperRefreshOnNextTick) {
            this.isAlreadyLaunchingSwiperRefreshOnNextTick = true;
            this.$nextTick(function () {
                const swiperContainer = document.querySelector(`#${this.uniqSwiperId}.swiper-container`);
                if (swiperContainer !== null) {
                    const swiper = swiperContainer.swiper;
                    if (swiper) {
                        swiper.updateSlides();
                        swiper.updateSize();
                        this.updateSwipeOptions(swiper);
                    } else {
                        // if swiper was removed
                        this.initSwiper();
                    }
                }
                this.isAlreadyLaunchingSwiperRefreshOnNextTick = false;
            })
        }
    }
}
</script>

<style lang="scss">

.text-align-right {
    text-align: right;
}

.swiper-container {
    padding-bottom: var(--swiper-container-padding-bottom);
    overflow-x: hidden;
    overflow-y: hidden;
}

.swiper-container .swiper-navigation {
    width: 100%;
    position: absolute;
    top: calc(50% - var(--swiper-container-padding-bottom) / 2);
    transition: top 0.35s ease 0s;
}

.swiper-container [class^="swiper-button-"] {
    &.swiper-button-hidden-when-disabled.swiper-button-disabled {
        display: none !important;
    }
}

.swiper-container .swiper-pagination {
    position: relative;
    max-width: 100%;
    padding-top: var(--swiper-pagination-padding-top);
}

.swiper-container .swiper-scrollbar {
    position: relative;
    margin-top: var(--swiper-scrollbar-margin-top);
}

.swiper-container .swiper-slide {
    padding-right: 1px;
}

</style>
